import { useRef, useState } from 'preact/hooks';
import { CODE_ALPHABET, CODE_LENGTH, NAME_MAX } from '@gazette/shared';
import { t, type MessageKey } from '../../i18n/index.ts';
import { actions } from '../../net/client.ts';
import { ErrorNotice, LangToggle, SoundToggle } from '../components/Chrome.tsx';

const NAME_KEY = 'gazette.name';

function inviteCode(): string | null {
  const code = new URLSearchParams(location.search).get('salon')?.toUpperCase() ?? '';
  return code.length === CODE_LENGTH && [...code].every((char) => CODE_ALPHABET.includes(char)) ? code : null;
}

function CodeInput({ value, onInput, inputRef }: { value: string; onInput: (code: string) => void; inputRef: { current: HTMLInputElement | null } }) {
  return (
    <div class="code-input" onClick={() => inputRef.current?.focus()}>
      <input
        ref={inputRef}
        id="case-code"
        name="code"
        class="code-input__field"
        value={value}
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        spellcheck={false}
        maxLength={CODE_LENGTH}
        onInput={(event) => {
          const cleaned = [...event.currentTarget.value.toUpperCase()].filter((char) => CODE_ALPHABET.includes(char)).join('');
          event.currentTarget.value = cleaned;
          onInput(cleaned);
        }}
      />
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <span class={`code-input__box ${index === Math.min(value.length, CODE_LENGTH - 1) ? 'is-next' : ''}`} aria-hidden="true">
          {value[index] ?? ''}
        </span>
      ))}
    </div>
  );
}

export function Home() {
  const invite = inviteCode();
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [code, setCode] = useState(invite ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<MessageKey | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // Actions stay at full strength and explain what is missing when pressed: a greyed-out
  // button reads as broken on a phone held at arm's length.
  const submit = async (mode: 'create' | 'join') => {
    const trimmed = name.trim();
    if (busy) return;
    if (!trimmed) {
      setProblem('home.name.required');
      nameRef.current?.focus();
      return;
    }
    if (mode === 'join' && code.length !== CODE_LENGTH) {
      setProblem('home.code.required');
      codeRef.current?.focus();
      return;
    }
    setProblem(null);
    localStorage.setItem(NAME_KEY, trimmed);
    setBusy(true);
    try {
      await (mode === 'create' ? actions.create(trimmed) : actions.join(code, trimmed));
    } finally {
      setBusy(false);
    }
  };

  const primaryIsJoin = invite !== null;

  return (
    <main class="home">
      <header class="masthead" data-occlude>
        <div class="masthead__clip pinned">
          <h1 class="masthead__title">{t('app.name')}</h1>
          <p class="masthead__motto">{t('app.tagline')}</p>
        </div>
        <div class="masthead__tools">
          <LangToggle />
          <SoundToggle />
        </div>
      </header>

      <section class="dossier" data-occlude data-tab={t('lobby.case')} aria-labelledby="dossier-title">
        <div class="dossier__body">
        <form
          class="dossier__form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(primaryIsJoin || code.length > 0 ? 'join' : 'create');
          }}
        >
          <h2 id="dossier-title" class="visually-hidden">
            {primaryIsJoin ? t('home.joinInvite', { code: invite }) : t('home.create')}
          </h2>
          <label class="field">
            <span class="field__label">{t('home.name.label')}</span>
            <input
              ref={nameRef}
              class="typed typed--large"
              name="name"
              value={name}
              maxLength={NAME_MAX}
              autoComplete="given-name"
              placeholder={t('home.name.placeholder')}
              onInput={(event) => {
                setName(event.currentTarget.value);
                if (problem === 'home.name.required') setProblem(null);
              }}
              aria-invalid={problem === 'home.name.required'}
              aria-describedby={problem ? 'home-problem' : undefined}
            />
          </label>
          {problem && (
            <p id="home-problem" class="field__error" role="alert">
              {t(problem)}
            </p>
          )}

          {primaryIsJoin ? (
            <>
              <button type="submit" class="btn btn--ink btn--wide" disabled={busy}>
                {t('home.joinInvite', { code: invite })}
              </button>
              <button type="button" class="btn btn--link" disabled={busy} onClick={() => void submit('create')}>
                {t('home.createInstead')}
              </button>
            </>
          ) : (
            <>
              <button type="button" class="btn btn--ink btn--wide" disabled={busy} onClick={() => void submit('create')}>
                {t('home.create')}
              </button>
              <p class="dossier__or" aria-hidden="true">
                <span>{t('home.or')}</span>
              </p>
              <div class="dossier__join">
                <label class="field" for="case-code">
                  <span class="field__label">{t('home.code.label')}</span>
                </label>
                <CodeInput
                  value={code}
                  inputRef={codeRef}
                  onInput={(next) => {
                    setCode(next);
                    if (problem === 'home.code.required') setProblem(null);
                  }}
                />
                <button type="submit" class="btn btn--ink" disabled={busy}>
                  {t('home.join')}
                </button>
              </div>
            </>
          )}
          <ErrorNotice />
        </form>

        <details class="rules">
          <summary>{t('home.rules.title')}</summary>
          <ol>
            <li>{t('home.rules.1')}</li>
            <li>{t('home.rules.2')}</li>
            <li>{t('home.rules.3')}</li>
            <li>{t('home.rules.4')}</li>
            <li>{t('home.rules.5')}</li>
          </ol>
          <p>{t('home.rules.players')}</p>
        </details>
        </div>
      </section>
    </main>
  );
}
